/**
 * Serviço para integração com API NASA FIRMS (Fire Information for Resource Management System)
 * Documentação: API FIRMS – Monitoramento de incêndios por satélite
 */
import api from './api';

const FIRMS_SOURCES = {
  VIIRS_SNPP_NRT: 'VIIRS_SNPP_NRT',
  VIIRS_NOAA20_NRT: 'VIIRS_NOAA20_NRT',
  VIIRS_NOAA21_NRT: 'VIIRS_NOAA21_NRT',
  MODIS_NRT: 'MODIS_NRT',
  VIIRS_SNPP_SP: 'VIIRS_SNPP_SP',
  MODIS_SP: 'MODIS_SP',
};

export const firesService = {
  /**
   * Busca detecções de incêndio em uma área (bbox). Não persiste dados.
   * @param {string} bbox - Formato "west,south,east,north"
   * @param {number} days - Dias de histórico (1-5)
   * @param {string} source - Fonte FIRMS
   */
  scan: async (bbox, days = 1, source = FIRMS_SOURCES.VIIRS_SNPP_NRT) => {
    const response = await api.get('/fires/scan/', {
      params: { bbox, days, source },
    });
    return response.data;
  },

  /**
   * Lista áreas de monitoramento FIRMS.
   * @param {string} guardianAreaId - Opcional: filtra no backend por Área de Interesse (UUID)
   */
  listAreas: async (guardianAreaId = null) => {
    const params = {};
    if (guardianAreaId) {
      params.guardian_area = guardianAreaId;
    }
    const response = await api.get('/fires/areas/', { params });
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  },

  /**
   * Obtém detalhes de uma área
   * @param {string} uuid - ID da área
   */
  getArea: async (uuid) => {
    const response = await api.get(`/fires/areas/${uuid}/`);
    return response.data;
  },

  /**
   * Cria área com bbox explícita
   * @param {object} data - { name, bbox_west, bbox_south, bbox_east, bbox_north, active?, guardian_area? }
   */
  createArea: async (data) => {
    const response = await api.post('/fires/areas/', data);
    return response.data;
  },

  /**
   * Cria área com upload de arquivo KML/KMZ
   * @param {object} formData - FormData com name, kml_file, active?, guardian_area?
   */
  createAreaWithKml: async (formData) => {
    const response = await api.post('/fires/areas/', formData);
    return response.data;
  },

  /**
   * Atualiza área (PUT completo)
   */
  updateArea: async (uuid, data) => {
    const response = await api.put(`/fires/areas/${uuid}/`, data);
    return response.data;
  },

  /**
   * Atualiza área parcialmente (PATCH)
   */
  patchArea: async (uuid, data) => {
    const response = await api.patch(`/fires/areas/${uuid}/`, data);
    return response.data;
  },

  /**
   * Remove área
   */
  deleteArea: async (uuid) => {
    const response = await api.delete(`/fires/areas/${uuid}/`);
    return response.data;
  },

  /**
   * Executa scan em área cadastrada e persiste resultado
   * @param {string} uuid - ID da área
   * @param {number} days - Dias (1-5)
   * @param {string|null} source - Fonte FIRMS opcional; omitido = FIRMS_DEFAULT_SOURCES (multi-fonte)
   */
  runAreaScan: async (uuid, days = 1, source = null) => {
    const params = { days };
    if (source) {
      params.source = source;
    }
    const response = await api.post(`/fires/areas/${uuid}/scan/`, null, {
      params,
    });
    return response.data;
  },

  /**
   * Lista resultados de scans de uma área
   * @param {string} uuid - ID da área
   * @param {number} limit - Máximo de registros (até 100)
   */
  listAreaScans: async (uuid, limit = 20) => {
    const response = await api.get(`/fires/areas/${uuid}/scans/`, {
      params: { limit },
    });
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.results)) return raw.results;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  },

  /**
   * Extrai bbox de arquivo KML/KMZ
   * @param {File} file - Arquivo .kml ou .kmz
   */
  extractBboxFromKml: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/fires/kml/extract-bbox', formData);
    return response.data;
  },

  FIRMS_SOURCES,
};
